import React, { useState, useEffect, useRef } from 'react';
import { TimerMode, TimerSettings } from '../types';
import { PlayIcon, StopIcon } from './ui/Icons';

interface TimerProps {
  mode: TimerMode;
  settings: TimerSettings;
  onSettingsChange: (settings: TimerSettings) => void;
  onTimerComplete: (duration: number, startTime: string, endTime: string) => void;
  onTimerStart: () => void;
  onModeChange: (mode: TimerMode) => void;
}

const Timer: React.FC<TimerProps> = ({ mode, settings, onSettingsChange, onTimerComplete, onTimerStart, onModeChange }) => {
  const [timeLeft, setTimeLeft] = useState(settings.focusDuration * 60);
  const [isActive, setIsActive] = useState(false);
  const [initialTime, setInitialTime] = useState(settings.focusDuration * 60);
  
  const startTimeRef = useRef<string | null>(null);
  const targetTimeRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Determine current duration based on mode for input display
  const currentDuration = mode === 'focus' ? settings.focusDuration : settings.restDuration;
  const presets = mode === 'focus' ? [5, 15, 30, 45, 60] : [1, 5, 10, 15];

  // Play a pleasant beep
  const playAlarm = () => {
    try {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        // Attempt to resume if suspended (common in browsers)
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
        osc.frequency.exponentialRampToValueAtTime(1046.5, ctx.currentTime + 0.1); // C6
        
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.5);
    } catch(e) {
        console.error("Audio playback failed", e);
    }
  };

  // Reset timer display when mode changes or settings change (if not active)
  useEffect(() => {
    if (!isActive) {
        const newDuration = mode === 'focus' ? settings.focusDuration : settings.restDuration;
        const seconds = newDuration * 60;
        setTimeLeft(seconds);
        setInitialTime(seconds);
        targetTimeRef.current = null;
    }
  }, [mode, settings.focusDuration, settings.restDuration, isActive]);

  // Main Timer Loop using Date.now() for drift correction / background support
  useEffect(() => {
    let interval: any = null;
    
    if (isActive) {
      if (!startTimeRef.current) startTimeRef.current = new Date().toISOString();
      
      // If we don't have a target time yet (just started), calculate it
      if (!targetTimeRef.current) {
          targetTimeRef.current = Date.now() + (timeLeft * 1000);
      }

      interval = setInterval(() => {
        // Safety check: if component unmounted or state invalid
        if (!targetTimeRef.current) return;

        const now = Date.now();
        const remaining = Math.ceil((targetTimeRef.current - now) / 1000);
        
        if (remaining <= 0) {
            // Timer finished
            setTimeLeft(0);
            // We let the completion effect handle the logic to keep this clean
        } else {
            setTimeLeft(remaining);
        }
      }, 500); // Check every 500ms for responsiveness
    } else {
      clearInterval(interval);
      targetTimeRef.current = null;
    }
    return () => clearInterval(interval);
  }, [isActive]); 

  // Watch for completion
  useEffect(() => {
    if (isActive && timeLeft <= 0) {
        playAlarm();
        setIsActive(false);
        const endTime = new Date().toISOString();
        onTimerComplete(initialTime, startTimeRef.current!, endTime);
        
        startTimeRef.current = null;
        targetTimeRef.current = null;
    }
  }, [isActive, timeLeft, initialTime, onTimerComplete]);

  const startTimer = () => {
    // Notify parent to handle unplanned gap detection
    onTimerStart();

    // Set target time based on current timeLeft to allow accurate background counting
    targetTimeRef.current = Date.now() + (timeLeft * 1000);
    setIsActive(true);
    
    // Initialize audio context on user interaction
    if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
    }
  };

  const stopTimer = () => {
    setIsActive(false);
    targetTimeRef.current = null;
    
    if (startTimeRef.current) {
        // Manual stop - log partial session
        const endTime = new Date().toISOString();
        // Calculate actual duration based on UI state (safe because timeLeft is updated via Date.now)
        const duration = initialTime - timeLeft;
        
        // Log if any time has passed
        if (duration > 0) {
            onTimerComplete(duration, startTimeRef.current, endTime);
        }
        startTimeRef.current = null;
    }
    
    // Reset display immediately for the current mode
    const newDuration = mode === 'focus' ? settings.focusDuration : settings.restDuration;
    setTimeLeft(newDuration * 60);
  };

  const handleDurationChange = (minutes: number) => {
    const safeMinutes = isNaN(minutes) ? 0 : minutes;
    const val = Math.max(1, Math.min(999, safeMinutes));
    if (mode === 'focus') {
        onSettingsChange({...settings, focusDuration: val});
    } else {
        onSettingsChange({...settings, restDuration: val});
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress circle calculation
  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  // Protect against division by zero if initialTime is 0 (unlikely)
  const safeInitial = initialTime > 0 ? initialTime : 1; 
  const progress = timeLeft / safeInitial;
  const dashoffset = circumference * (1 - progress);

  const themeColor = mode === 'focus' ? 'text-emerald-500' : 'text-amber-500';
  const bgColor = mode === 'focus' ? 'bg-emerald-50' : 'bg-amber-50';
  const strokeColor = mode === 'focus' ? '#10b981' : '#f59e0b';

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto">
      
      {/* Controls Container - Fades out when active */}
      <div className={`w-full transition-all duration-300 ${isActive ? 'opacity-40 pointer-events-none filter blur-[1px]' : 'opacity-100'}`}>
        
        {/* Mode Tabs */}
        <div className="flex p-1 bg-slate-100 rounded-xl mb-6">
            <button 
                onClick={() => onModeChange('focus')} 
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'focus' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
                Focus
            </button>
            <button 
                onClick={() => onModeChange('rest')} 
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'rest' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
                Rest
            </button>
        </div>

        {/* Duration Selection */}
        <div className="mb-8">
            <div className="flex items-center justify-between mb-3 px-1">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Duration</label>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1 focus-within:ring-2 focus-within:ring-indigo-100 focus-within:border-indigo-300 transition-all">
                    <input
                        type="number"
                        min="1"
                        max="999"
                        value={currentDuration}
                        onChange={(e) => handleDurationChange(parseInt(e.target.value))}
                        className="w-12 text-right outline-none font-bold text-slate-700 bg-transparent text-sm"
                    />
                    <span className="text-xs text-slate-400 font-medium">min</span>
                </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
                {presets.map(min => (
                    <button
                        key={min}
                        onClick={() => handleDurationChange(min)}
                        className={`flex-1 min-w-[3rem] px-2 py-2 rounded-lg text-sm font-medium transition-all border ${
                            currentDuration === min 
                            ? (mode === 'focus' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200')
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                    >
                        {min}
                    </button>
                ))}
            </div>
        </div>
      </div>

      {/* Timer Display */}
      {/* Expanded viewBox to 320 to provide generous padding for stroke */}
      <div className="relative mb-10 w-full max-w-[18rem] aspect-square mx-auto">
        <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 320 320">
            <circle
                cx="160"
                cy="160"
                r={radius}
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-slate-100"
            />
            <circle
                cx="160"
                cy="160"
                r={radius}
                stroke={strokeColor}
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={dashoffset}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-linear"
            />
        </svg>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full">
            <div className={`text-5xl sm:text-6xl font-bold tracking-tighter ${themeColor}`}>
                {formatTime(timeLeft)}
            </div>
            <div className={`mt-2 text-sm font-medium px-3 py-1 rounded-full inline-block ${bgColor} ${themeColor} uppercase tracking-wide`}>
                {mode === 'focus' ? 'Focus Time' : 'Rest Time'}
            </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-4">
        {!isActive ? (
             <button
                onClick={startTimer}
                className={`flex items-center justify-center w-20 h-20 rounded-full shadow-xl transition-all transform hover:scale-105 active:scale-95 text-white ${
                     mode === 'focus' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'
                }`}
                aria-label="Start Timer"
            >
                <PlayIcon className="w-8 h-8 ml-1" />
            </button>
        ) : (
            <button
                onClick={stopTimer}
                className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50 text-red-500 border-2 border-red-100 hover:bg-red-100 hover:border-red-200 shadow-lg transition-all transform hover:scale-105 active:scale-95"
                aria-label="Stop Timer"
            >
                <StopIcon className="w-8 h-8" />
            </button>
        )}
      </div>

    </div>
  );
};

export default Timer;